import React from 'react';

import { useTranslations } from '~src/commons/utils/translations/translate';

import {
  getPostalCodeFieldConfig,
  getPostalCodePrefix,
  getPostalCodeValidationRegexByCountryCode,
} from '~address-form/validation/postal-code-validation';

import { useGlobalState } from '../../global-state';
import { Field } from '../../ui/field';
import { InputMask } from '../../ui/input-mask';
import { TextInputProps } from '../../ui/text-input';

interface PostalCodeFieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'children'> {
  children: (inputProps: TextInputProps, value: any) => React.ReactElement;
  name: string;
  required?: boolean;
}

export const PostalCodeField: React.FC<PostalCodeFieldProps> = ({
  children,
  name,
  required,
  ...otherProps
}) => {
  const { tICU } = useTranslations();
  const globalState = useGlobalState();

  const country = globalState.purchase_country;

  const postalCodeConfig = getPostalCodeFieldConfig(country);

  return (
    <Field
      name={name}
      normalize={getPostalCodeNormalize(country)}
      rules={[
        {
          required,
          message: tICU('widget.ADDRESS_MODAL.POSTAL_CODE_REQUIRED_VALIDATION'),
        },
        {
          pattern: getPostalCodeValidationRegexByCountryCode(country),
          message: tICU('widget.ADDRESS_MODAL.POSTAL_CODE_VALIDATION'),
        },
      ]}
    >
      {(props, meta) => (
        <InputMask
          maskOptions={{
            mask: postalCodeConfig.mask,
          }}
          {...props}
          {...otherProps}
          value={addPostalCodePrefix(props.value, country)}
        >
          {maskProps => {
            const inputProps = {
              autoComplete: 'postal-code',
              error: meta.errors[0],
              name: 'postal_code',
              type: postalCodeConfig.inputType,
              ...maskProps,
            };
            return children(inputProps, props.value);
          }}
        </InputMask>
      )}
    </Field>
  );
};

export function getPostalCodeNormalize(country: string) {
  return (postalCode: string) => {
    return toUpperCase(removePostalCodePrefix(postalCode, country));
  };
}

export function toUpperCase(value: string | number | undefined) {
  if (typeof value !== 'string') {
    return value;
  }
  return value.toUpperCase();
}

// Carriers and external services expect the postal code in Latvia without the prefix
// while users still try to put it in. That's why we use a mask and remove it here
export function removePostalCodePrefix(postalCode: string, country: string) {
  const prefix = getPostalCodePrefix(country);

  if (prefix === undefined || typeof postalCode !== 'string') {
    return postalCode;
  }

  return postalCode.replace(`${prefix}-`, '');
}

export function addPostalCodePrefix(postalCode: string, country: string) {
  if (!postalCode) return '';
  const prefix = getPostalCodePrefix(country);

  if (prefix === undefined || typeof postalCode !== 'string' || postalCode.startsWith(prefix)) {
    return postalCode;
  }

  return `${prefix}-${postalCode}`;
}
